import classNames from 'classnames'
import React, { useCallback, useState } from 'react'
import InView from 'react-intersection-observer'
import { useTranslations } from '../hooks/useTranslations'
import { InternalExternalLink } from '../types/InternalExternalLink'
import { isNullOrWhiteSpace, sanitizeHtmlAndReplaceNewlines } from '../utilities/string'
import { Button } from './Button'
import { Grid } from './Grid'
import { ImageProps } from './Image'
import { Link } from './Link'
import { MaximizeButton } from './MaximizeButton'
import { PauseButton } from './PauseButton'
import { SoundButton } from './SoundButton'
import {
  AspectRatio,
  videoAspectRatioClasses,
  VideoAspectRatioWrapper,
} from './VideoAspectRatio'
import { VideoModal } from './VideoModal'
import { VimeoVideo } from './VimeoVideo'

export interface VideoTeaserProps {
  additionalClassName?: string
  buttonLabel?: string
  description?: string
  leftVideoBackgroundColor?: string
  loading?: 'eager' | 'lazy'
  orientation?: 'video-left' | 'video-right'
  playVideoCallback?: (videoUrl: string) => void
  previewImage?: ImageProps
  /**
   * Vimeo only
   */
  previewVideoUrl?: string
  videoControls: boolean
  title: string
  /**
   * Youtube or Vimeo
   */
  videoUrl?: string
  link?: InternalExternalLink
  showVideoModalButton?: boolean
}

export const VideoTeaser = ({
  additionalClassName,
  buttonLabel,
  description,
  leftVideoBackgroundColor = 'bg-shade-lightest-warm',
  loading = 'lazy',
  orientation = 'video-right',
  playVideoCallback,
  previewImage,
  previewVideoUrl,
  videoControls = false,
  link,
  title,
  videoUrl,
  showVideoModalButton = true,
}: VideoTeaserProps) => {
  const translations = useTranslations()
  const [videoPlaying, setVideoPlaying] = useState(false)
  const [started, setStarted] = useState(false)
  const [paused, setPaused] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(false)

  const onPause = useCallback(() => {
    setPaused(prev => !prev)
  }, [])

  const playVideo = () => {
    if (playVideoCallback && !isNullOrWhiteSpace(videoUrl)) {
      // let parent control decide how to play the video (e.g. shared VideoModal)
      playVideoCallback(videoUrl)
    } else {
      // use local video modal
      setVideoPlaying(true)
    }
  }

  const leftColumnOffset =
    'sm:col-start-2 md:col-start-1 lg:col-start-1 xl:col-start-2 xxl:col-start-4'

  return (
    <Grid
      additionalClassName={classNames(
        'py-45 tb:py-90 sm:py-120',
        'tb:items-center',
        leftVideoBackgroundColor && orientation === 'video-left'
          ? leftVideoBackgroundColor
          : '',
        additionalClassName
      )}
    >
      <div
        className={classNames(
          leftColumnOffset,
          'col-span-full sm:col-span-4 md:col-span-6 lg:col-span-8',
          'tb:mx-5 sm:-mx-120 md:mx-0 lg:px-90 xl:px-30',
          'flex flex-col tb:flex-row tb:items-center tb:justify-between',
          'gap-20 tb:gap-45 sm:gap-75 md:gap-120'
        )}
      >
        <div
          className={classNames(
            'w-full tb:w-330 sm:w-480 md:w-[34.375rem] lg:w-[40rem] xl:w-[43.75rem] flex-shrink-0',
            'focus:outline-none',
            {
              'tb:order-last': orientation === 'video-right',
              'cursor-pointer': !isNullOrWhiteSpace(videoUrl),
            }
          )}
          onClick={() => playVideo()}
          tabIndex={-1}
        >
          <InView triggerOnce rootMargin="0px 0px 500px 0px">
            {({ inView, ref }) => (
              <div ref={ref}>
                <VideoAspectRatioWrapper aspectRatio={AspectRatio['16/9']}>
                  {(inView || loading === 'eager') && (
                    <VimeoVideo
                      className={videoAspectRatioClasses}
                      imgElementClassName="w-full"
                      paused={!inView || paused} // keep paused until scrolled into view the first time
                      previewImage={previewImage}
                      muted={!soundEnabled}
                      onBackgroundVideoStarted={() => setStarted(true)}
                      title=""
                      videoUrl={previewVideoUrl ?? ''}
                    />
                  )}
                  {started && (
                    <div
                      className={classNames(
                        'absolute flex gap-15 z-10 top-0 right-0 m-15'
                      )}
                    >
                      <PauseButton onClick={onPause} />
                      {videoControls && (
                        <SoundButton
                          active={soundEnabled}
                          onClick={() => setSoundEnabled(old => !old)}
                        />
                      )}
                      {videoControls && <MaximizeButton onClick={playVideo} />}
                    </div>
                  )}
                </VideoAspectRatioWrapper>
              </div>
            )}
          </InView>
        </div>

        <div
          className={classNames('tb:flex', {
            'justify-end': orientation === 'video-left',
          })}
        >
          <div className="sm:max-w-330 lg:max-w-480">
            <h2
              className={classNames(
                'mb-10 font-h3 text-h3 text-secondary-dark',
                'tb:mb-15'
              )}
            >
              {title}
            </h2>

            {!isNullOrWhiteSpace(description) && (
              <p
                className="font-body text-body text-secondary-darker"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlAndReplaceNewlines(description),
                }}
              />
            )}

            {!playVideoCallback && !isNullOrWhiteSpace(videoUrl) && (
              <VideoModal
                onClose={() => setVideoPlaying(false)}
                open={videoPlaying}
                videoUrl={videoUrl}
              />
            )}
            <div className="flex flex-col items-center tb:flex-row gap-15 mt-30">
              {!isNullOrWhiteSpace(videoUrl) && showVideoModalButton && (
                <Button onClick={() => playVideo()}>
                  {isNullOrWhiteSpace(buttonLabel)
                    ? translations.learnMore ?? 'Learn more'
                    : buttonLabel}
                </Button>
              )}
              {!!link && (
                <Link className="text-primary font-medium" {...link}>
                  {link.title}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </Grid>
  )
}
